/**
 * NextActionHero — single-screen, single-action focus card for the Dashboard.
 *
 * Replaces the old multi-stat StudyNowHero. The principle: a busy student
 * has 15 minutes between classes. Don't show them a control room — show
 * them ONE button that turns those 15 minutes into progress.
 *
 * Calls GET /api/intelligence/next-action?minutes={N} where N is selected
 * via the time-budget pill (5 / 15 / 30 min). Tone is friendly/coach.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Loader2 } from 'lucide-react';
import api, { apiUrl } from '../utils/apiClient';

const TIME_OPTIONS = [5, 15, 30];

const SkeletonHero = () => (
  <div
    className="relative overflow-hidden mb-6 px-6 py-8 sm:px-10 sm:py-10 bg-[var(--isp-glass)] border border-[var(--isp-glass-border)]"
    data-testid="next-action-hero-skeleton"
  >
    <div className="flex items-center gap-3 text-sm text-[var(--isp-muted)]">
      <Loader2 className="w-4 h-4 animate-spin" /> Building your next move…
    </div>
  </div>
);

const NextActionHero = ({ defaultMinutes = 15 }) => {
  const [minutes, setMinutes] = useState(defaultMinutes);
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAction = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(apiUrl(`/study-intelligence/next-action?minutes=${minutes}`));
      setAction(data);
    } catch {
      setAction(null);
    } finally {
      setLoading(false);
    }
  }, [minutes]);

  useEffect(() => { fetchAction(); }, [fetchAction]);

  if (loading && !action) return <SkeletonHero />;
  if (!action) return null;

  return (
    <motion.div
      key={action.kind + minutes}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="relative overflow-hidden mb-6 bg-[var(--isp-glass)] border border-[var(--isp-glass-border)]"
      data-testid="next-action-hero"
    >
      {/* Klein-blue accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#002FA7]" />

      <div className="relative px-6 py-8 sm:px-10 sm:py-10">
        {/* Time-budget chooser */}
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-3.5 h-3.5 text-[var(--isp-muted)]" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--isp-muted)]">
            I have
          </span>
          <div className="flex items-center gap-1" data-testid="next-action-time-chooser">
            {TIME_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => setMinutes(opt)}
                className={`px-2.5 py-0.5 text-[11px] font-semibold tracking-tight transition-colors border ${
                  opt === minutes
                    ? 'bg-[#002FA7] border-[#002FA7] text-white'
                    : 'bg-transparent border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:border-[var(--isp-border-strong)]'
                }`}
                data-testid={`next-action-time-${opt}`}
              >
                {opt}m
              </button>
            ))}
          </div>
        </div>

        {/* Headline */}
        <h2
          className="text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] tracking-tight leading-tight"
          data-testid="next-action-headline"
        >
          {action.headline}
        </h2>

        {/* Subline */}
        {action.subline && (
          <p className="mt-2 text-sm sm:text-base text-[var(--isp-body)] max-w-2xl" data-testid="next-action-subline">
            {action.subline}
          </p>
        )}

        {/* Primary CTA */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to={action.cta_route || '/'}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold text-sm tracking-tight transition-colors"
            data-testid="next-action-cta"
          >
            {action.cta_label || 'Start'}
            <ArrowRight className="w-4 h-4" />
          </Link>

          {/* If there are more cards beyond what fits in the budget, surface that. */}
          {action.kind === 'flashcards' && (action.secondary_count || 0) > 0 && (
            <span className="text-xs text-[var(--isp-muted)]" data-testid="next-action-overflow">
              +{action.secondary_count} more for later
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default NextActionHero;
